import React, { useState, useEffect } from 'react';
import axios from 'axios';

const HeroModal = ({ isOpen, onClose, initialData }) => {
    const [heroData, setHeroData] = useState({
        title: "",
        subtitle: "",
        ctaPrimary: { text: "", link: "" },
        ctaSecondary: { text: "", link: "" },
        heroImage: "",
        services: Array(3).fill({ name: "", icon: "" }),
        stats: Array(3).fill({ value: "", label: "" })
    });

    const [activeTab, setActiveTab] = useState('main');
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    // Fetch current data when modal opens
    useEffect(() => {
        const fetchCurrentData = async () => {
            if (isOpen && !initialData) {
                try {
                    setIsLoading(true);
                    const response = await fetch(`${import.meta.env.VITE_API_URL}/home/hero/`);
                    if (response.ok) {
                        const data = await response.json();
                        setHeroData(data);
                    }
                } catch (error) {
                    console.error('Error fetching hero data:', error);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        fetchCurrentData();
    }, [isOpen, initialData]);

    // If initialData is provided, use that instead of fetching
    useEffect(() => {
        if (initialData) {
            setHeroData(initialData);
        }
    }, [initialData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setHeroData(prev => ({ ...prev, [name]: value }));
    };

    const handleNestedChange = (key, subKey, value) => {
        setHeroData(prev => ({
            ...prev,
            [key]: { ...prev[key], [subKey]: value }
        }));
    };

    const handleServiceChange = (index, field, value) => {
        const updatedServices = [...heroData.services];
        updatedServices[index] = { ...updatedServices[index], [field]: value };
        setHeroData(prev => ({ ...prev, services: updatedServices }));
    };

    const handleStatChange = (index, field, value) => {
        const updatedStats = [...heroData.stats];
        updatedStats[index] = { ...updatedStats[index], [field]: value };
        setHeroData(prev => ({ ...prev, stats: updatedStats }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('image', file);
        formData.append('category', 'hero_images');

        try {
            setIsUploading(true);
            setUploadProgress(0);

            const response = await axios.post(`${import.meta.env.VITE_API_URL}/images/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );
                    setUploadProgress(percentCompleted);
                }
            });

            // Update the heroImage with the URL from the response
            setHeroData(prev => ({
                ...prev,
                heroImage: response.data.image || response.data.image.url
            }));
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image. Please try again.');
        } finally {
            setIsUploading(false);
            setUploadProgress(0);
        }
    };

    const handleSubmit = async () => {
        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/home/hero/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(heroData)
            });

            if (response.ok) {
                const updatedData = await response.json();
                window.location.reload(); // Only reload if needed
            } else {
                console.error('Failed to update hero data');
                alert('Failed to save changes. Please try again.');
            }
        } catch (error) {
            console.error('Error updating hero data:', error);
            alert('An error occurred while saving. Please try again.');
        }
    };

    if (isLoading) {
        return (
            <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
                <div className="modal-box">
                    <span className="loading loading-spinner loading-lg"></span>
                    <p>Loading hero data...</p>
                </div>
            </dialog>
        );
    }

    return (
        <dialog className={`modal ${isOpen ? 'modal-open' : ''}`}>
            <div className="modal-box w-11/12 max-w-4xl max-h-[90vh] relative">
                <button onClick={onClose} className="btn btn-sm btn-circle absolute right-2 top-2">✕</button>
                <h3 className="font-bold text-lg mb-4">Edit Hero Section</h3>
                
                {/* Tab Navigation */}
                <div className="tabs tabs-boxed mb-6">
                    <button 
                        className={`tab ${activeTab === 'main' ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab('main')}
                    >
                        Main Content
                    </button>
                    <button 
                        className={`tab ${activeTab === 'services' ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab('services')}
                    >
                        Services
                    </button>
                    <button 
                        className={`tab ${activeTab === 'stats' ? 'tab-active' : ''}`}
                        onClick={() => setActiveTab('stats')}
                    >
                        Stats
                    </button>
                </div>
                
                <div className="overflow-y-auto max-h-[60vh] pr-2">
                    {/* Main Content Tab */}
                    {activeTab === 'main' && (
                        <div className="space-y-4">
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Title</span>
                                </label>
                                <input 
                                    type="text" 
                                    name="title" 
                                    value={heroData.title || ''} 
                                    onChange={handleChange} 
                                    className="input input-bordered w-full" 
                                />
                            </div>
                            
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Subtitle</span>
                                </label>
                                <input 
                                    type="text" 
                                    name="subtitle" 
                                    value={heroData.subtitle || ''} 
                                    onChange={handleChange} 
                                    className="input input-bordered w-full" 
                                />
                            </div>
                            
                            <div className="form-control">
                                <label className="label">
                                    <span className="label-text">Hero Image</span>
                                </label>
                                <div className="flex items-center gap-4">
                                    <input 
                                        type="file" 
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="file-input file-input-bordered w-full max-w-xs"
                                        disabled={isUploading}
                                    />
                                    {isUploading && (
                                        <progress 
                                            className="progress progress-primary w-56" 
                                            value={uploadProgress} 
                                            max="100"
                                        ></progress>
                                    )}
                                </div>
                                {heroData.heroImage && (
                                    <div className="mt-2">
                                        <div className="text-sm text-gray-500 mb-1">Current Image:</div>
                                        <img 
                                            src={heroData.heroImage} 
                                            alt="Hero preview" 
                                            className="max-h-40 rounded-md border"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'https://via.placeholder.com/800x400?text=Image+Not+Found';
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Primary CTA Text</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        value={heroData.ctaPrimary?.text || ''} 
                                        onChange={(e) => handleNestedChange("ctaPrimary", "text", e.target.value)} 
                                        className="input input-bordered w-full" 
                                    />
                                </div>
                                
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Primary CTA Link</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        value={heroData.ctaPrimary?.link || ''} 
                                        onChange={(e) => handleNestedChange("ctaPrimary", "link", e.target.value)} 
                                        className="input input-bordered w-full" 
                                    />
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Secondary CTA Text</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        value={heroData.ctaSecondary?.text || ''} 
                                        onChange={(e) => handleNestedChange("ctaSecondary", "text", e.target.value)} 
                                        className="input input-bordered w-full" 
                                    />
                                </div>
                                
                                <div className="form-control">
                                    <label className="label">
                                        <span className="label-text">Secondary CTA Link</span>
                                    </label>
                                    <input 
                                        type="text" 
                                        value={heroData.ctaSecondary?.link || ''} 
                                        onChange={(e) => handleNestedChange("ctaSecondary", "link", e.target.value)} 
                                        className="input input-bordered w-full" 
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {/* Services Tab */}
                    {activeTab === 'services' && (
                        <div className="space-y-4">
                            {heroData.services?.map((service, index) => (
                                <div key={index} className="card bg-base-100 shadow-sm p-4">
                                    <h4 className="font-bold mb-3">Service {index + 1}</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text">Name</span>
                                            </label>
                                            <input 
                                                type="text" 
                                                value={service?.name || ''} 
                                                onChange={(e) => handleServiceChange(index, 'name', e.target.value)} 
                                                className="input input-bordered w-full" 
                                            />
                                        </div>
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text">Icon (emoji)</span>
                                            </label>
                                            <input 
                                                type="text" 
                                                value={service?.icon || ''} 
                                                onChange={(e) => handleServiceChange(index, 'icon', e.target.value)} 
                                                className="input input-bordered w-full" 
                                                maxLength="2"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    {/* Stats Tab */}
                    {activeTab === 'stats' && (
                        <div className="space-y-4">
                            {heroData.stats?.map((stat, index) => (
                                <div key={index} className="card bg-base-100 shadow-sm p-4">
                                    <h4 className="font-bold mb-3">Stat {index + 1}</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text">Value</span>
                                            </label>
                                            <input 
                                                type="text" 
                                                value={stat?.value || ''} 
                                                onChange={(e) => handleStatChange(index, 'value', e.target.value)} 
                                                className="input input-bordered w-full" 
                                            />
                                        </div>
                                        <div className="form-control">
                                            <label className="label">
                                                <span className="label-text">Label</span>
                                            </label>
                                            <input 
                                                type="text" 
                                                value={stat?.label || ''} 
                                                onChange={(e) => handleStatChange(index, 'label', e.target.value)} 
                                                className="input input-bordered w-full" 
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                
                <div className="modal-action mt-4">
                    <button className="btn" onClick={onClose}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSubmit}>
                        Save Changes
                    </button>
                </div>
            </div>
        </dialog>
    );
};

export default HeroModal;